import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ApiFactory from './factory';
import { AuthState, Store } from 'store/types';
import {
    ApiClass,
    ApiClassConstructor,
    LocalesResponse,
    TranslationResponse,
} from './types';

import { setNetworkError } from '../store/reducers/settings';
import { useResponseHandler } from '../utils/response';

import AuthApi from './auth';
import AdminApi from './admin';
import CmsApi from './cms';
import UserApi from './user';
import InfoApi from './info';
import FormApi from './form';
import SearchApi from './search';
import PartnerApi from './partner';
import CategoryApi from './category';
import CronjobApi from './cronjob';
import ChatApi from './chat';
import OrderApi from './order';
import PropertyApi from './property';
import ThriftShopApi from './thriftshop';
import CalendarApi from './calendar';

type ApiFunction<T> = (...params: any[]) => Promise<T | null>;
type ApiResponse<T> = {
    loading: boolean;
    data?: T;
    refresh: () => void;
};

const API_FACTORY = new ApiFactory(AuthApi);

export function useApiLoader<T>(
    apiFunction: ApiFunction<T>,
    ...params: any[]
): ApiResponse<T> {
    const { handleResponse } = useResponseHandler();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<T>();

    const refresh = () => {
        setLoading(true);
        apiFunction(...params)
            .then(response => {
                if (response) {
                    handleResponse(response, () => {
                        setData(response);
                        setLoading(false);
                    });
                }
            })
            .catch(() => setLoading(false));
    };

    useEffect(refresh, [apiFunction, ...params]);

    return useMemo<ApiResponse<T>>(
        () => ({
            loading,
            data,
            refresh,
        }),
        [loading],
    );
}

const useApi = () => {
    const factoryRef = useRef(API_FACTORY);
    const dispatch = useDispatch();
    factoryRef.current.updateDispatchFunction(dispatch);

    const session = useSelector<Store, AuthState>(state => state.auth);

    useEffect(() => {
        factoryRef.current.updateSession(session);
    }, [session]);

    const hasNetworkError = () => {
        dispatch(setNetworkError({ error: true }));
    };

    const clearNetworkError = () => {
        dispatch(setNetworkError({ error: false }));
    };

    function buildApi<ApiType>(
        apiClass: ApiClassConstructor<ApiType>,
    ): ApiClass<ApiType> {
        return apiClass(factoryRef.current);
    }

    const getLocales = async (
        onlyLang: boolean = true,
    ): Promise<LocalesResponse | null> => {
        const localeHeaders: Headers = new Headers({
            'Content-Type': 'application/json',
        });

        const localSession = factoryRef.current.getSession();
        if (
            localSession &&
            localSession.isAuthenticated &&
            localSession.auth?.access_token
        ) {
            localeHeaders.set(
                'Authorization',
                `Bearer ${localSession.auth.access_token}`,
            );
        }

        try {
            const response = await fetch(
                new URL(
                    `/locales?onlyLang=${onlyLang.toString()}`,
                    factoryRef.current.getApiHost(),
                ),
                {
                    method: 'GET',
                    headers: localeHeaders,
                    mode: 'cors',
                },
            );

            return await response.json();
        } catch (e) {
            hasNetworkError();
            return null;
        }
    };

    const getTranslations = async (
        locale: string,
    ): Promise<TranslationResponse | null> => {
        factoryRef.current.updateRequestLocale(locale);

        let type: 'small' | 'full' = 'small';
        const translationHeaders: Headers = new Headers({
            'Content-Type': 'application/json',
        });

        const localSession = factoryRef.current.getSession();
        if (
            localSession &&
            localSession.isAuthenticated &&
            localSession.auth?.access_token
        ) {
            type = 'full';
            translationHeaders.set(
                'Authorization',
                `Bearer ${localSession.auth.access_token}`,
            );
        }

        try {
            const response = await fetch(
                new URL(
                    `/${locale}/translation`,
                    factoryRef.current.getApiHost(),
                ),
                {
                    method: 'GET',
                    headers: translationHeaders,
                    mode: 'cors',
                },
            );

            clearNetworkError();

            return {
                type,
                loadedLocale: locale,
                data: await response.json(),
            };
        } catch (e) {
            hasNetworkError();
            return null;
        }
    };

    return useMemo(() => {
        return {
            getLocales,
            getTranslations,
            auth: buildApi(AuthApi),
            info: buildApi(InfoApi),
            form: buildApi(FormApi),
            user: buildApi(UserApi),
            admin: buildApi(AdminApi),
            cms: buildApi(CmsApi),
            calendar: buildApi(CalendarApi),
            search: buildApi(SearchApi),
            partner: buildApi(PartnerApi),
            category: buildApi(CategoryApi),
            order: buildApi(OrderApi),
            cronjob: buildApi(CronjobApi),
            chat: buildApi(ChatApi),
            property: buildApi(PropertyApi),
            thriftshop: buildApi(ThriftShopApi),
        };
    }, []);
};

export default useApi;
